import { ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { AddEditRevenueDialogData, FeedStockLoadAdditionalRevenue, IdName } from '../../../interfaces/pricematrixmodel';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { LookUpService } from '../../../services/lookupservice';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FeedstockService } from '../../../services/feedstockservice';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { ExpenseTypeResponse } from '../../../interfaces/feeCategoryLookupResponse';
import { AddEditRevenueDialogComponent } from './add-edit-revenue-dialog/add-edit-revenue-dialog.component';
import { RolePermission, RolePermissionsService } from '../../../services/role-permissions.service';
import { ActionStatus, ScreenName } from '../../../interfaces/securityPermissions';
import { Action } from 'rxjs/internal/scheduler/Action';
import { DragDropModule } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-add-edit-revenue',
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatDialogModule,
    MatCardModule,
    MatMenuModule,
    DragDropModule
  ],
  templateUrl: './add-edit-revenue.component.html',
  styleUrl: './add-edit-revenue.component.scss'
})
export class AddEditRevenueComponent implements OnInit {

  @ViewChild('revenueForm') revenueForm!: NgForm;
  fsLoadId: number;
  workOrderNumber: string;
  loadStatus: string;
  dataSource = new MatTableDataSource<FeedStockLoadAdditionalRevenue>([]);
  displayedColumns: string[] = ['actions', 'name', 'invoiceNumber', 'price', 'expenseType', 'expensive'];

  isAddingNewRow: boolean = false;
  editingRow: FeedStockLoadAdditionalRevenue | null = null;
  editForm!: FormGroup;

  expenseTypes: ExpenseTypeResponse[] = [];
  revenuePermissions = new ActionStatus();

  constructor(
    public dialogRef: MatDialogRef<AddEditRevenueComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddEditRevenueDialogData,
    private fb: FormBuilder,
    private feedstockService: FeedstockService,
    private lookUpService: LookUpService,
    private dialog: MatDialog,
    private cdRef: ChangeDetectorRef,
    private permissions: RolePermissionsService
  ) {
    console.log("incoming", data);
    this.fsLoadId = data.fsLoadId;
    this.workOrderNumber = data.workOrderNumber;
    this.loadStatus = data.fsStatus;
  }

  ngOnInit(): void {
    this.getExpenseTypesLookup();
    this.loadAdditionalRevenue();
    this.permissions.permissions$().subscribe(permissions => {
      if (permissions) {        
        const screenPermissions = permissions.filter(p => p.screenName === ScreenName.Expense);
        this.revenuePermissions = this.mapPermissionsToActionStatus(screenPermissions);
      }
    });
  }
  
  loadAdditionalRevenue(): void {
    this.feedstockService.getAdditionalRevenue(this.fsLoadId).subscribe({
      next: (data: FeedStockLoadAdditionalRevenue[]) => {
        this.dataSource.data = data;
        console.log('Additional Revenues Loaded:', data);
      }
    });
  }

  getExpenseTypesLookup(): void {
    this.lookUpService.getExpenseTypes().subscribe({
      next: (data: ExpenseTypeResponse[]) => {
        this.expenseTypes = data;
      }
    });
  }

  onAddRow(): void {
    const dialogRef = this.dialog.open(AddEditRevenueDialogComponent, {
      disableClose: true,
      data: {
        data: {
          id: 0,
          name: '',
          price: 0,
          invoiceNumber: '',
          expensive: false,
          expenseType: null,
          fsLoadScheduleId: this.fsLoadId
        },
        expenseTypes: this.expenseTypes,
        disableCheckbox: this.loadStatus === 'Invoiced'
      }
    });

    dialogRef.afterClosed().subscribe((result: FeedStockLoadAdditionalRevenue) => {
      if (result) {
        this.feedstockService.saveFeedStockLoadAdditionalRevenue(result).subscribe({
          next: () => {
            this.loadAdditionalRevenue();
          }
        });
      }
    });
  }

  onEditRow(row: FeedStockLoadAdditionalRevenue): void {
    const dialogRef = this.dialog.open(AddEditRevenueDialogComponent, {
      disableClose: true,
      data: {
        data: { ...row },
        expenseTypes: this.expenseTypes,
        disableCheckbox: this.loadStatus === 'Invoiced'
      }
    });

    dialogRef.afterClosed().subscribe((result: FeedStockLoadAdditionalRevenue) => {
      if (result) {
        this.feedstockService.saveFeedStockLoadAdditionalRevenue(result).subscribe({
          next: () => {
            this.loadAdditionalRevenue();
          }
        });
      }
    });
  }
  
  onDeleteRow(id: number, name: string | null): void {
    const dialogData: ConfirmationDialogData = {
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete "${name || 'this item'}"?`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'warn'
    };

    this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      panelClass: 'delete-confirmation-dialog',
      data: dialogData,
      disableClose: true
    }).afterClosed().subscribe(result => {
      if (result) {
        this.feedstockService.deleteFeedStockLoadAdditionalRevenue(id).subscribe({
          next: () => {
            this.loadAdditionalRevenue();
          }
        });
      }
    });
  }
  
  getExpenseTypeName(id: number | null): string {
    if (id === null || id === undefined) {
      return '';
    }
    const expenseType = this.expenseTypes.find(et => et.id === id);
    return expenseType ? expenseType.name : '';
  }
  
  onClose(): void {
    this.dialogRef.close(false);
  }
  
  trackByRevenueId(index: number, item: FeedStockLoadAdditionalRevenue): number {
    return item.id;
  }

  private mapPermissionsToActionStatus(permissions: RolePermission[]): ActionStatus {
    const actionStatus = new ActionStatus();
    for (const permission of permissions) {
      if (actionStatus.hasOwnProperty(permission.actionName.trim())) {
        (actionStatus as any)[permission.actionName.trim()] = permission.status;
      }
    }
    return actionStatus;
  }
}
